<script setup lang="ts">
import { convertApiThrowsToLocal } from '~/services/DartsThrowHelper'
import { getGameStore, GamePlayerSimple } from '~/store/localGameStore'

const emit = defineEmits(['refreshRequest', 'gameUpdated'])

const props = defineProps<{
  game: Game
}>()

const gameState = getGameStore(props.game.id.toString())
gameState.initFromApi(props.game)

const winner = computed(() => {
  if (props.game.winner_id) {
    return getPlayer(props.game.player1).id === props.game.winner_id ? props.game.player1 : props.game.player2
  }
  return null
})

const bullWinner = computed(() => {
  if (props.game.bull_winner) {
    return props.game.bull_winner === 1 ? props.game.player1 : props.game.player2
  }
  return null
})

const isLive = computed(() => {
  return props.game.board && !props.game.concluded_at && props.game.started_at
})

function getPlayerIndex(playerId: number) {
  return getPlayer(props.game.player1).id === playerId ? 1 : 2
}

function getOneEighties(playerId: number) {
  // If the game is concluded we get the information from there.
  if (props.game.concluded_at) {
    return getPlayer(props.game[`player${getPlayerIndex(playerId)}`]).one_eighties
  }

  // Otherwise we take it from the throws.
  let total180s = 0
  props.game.throws.forEach((dartsThrow) => {
    if (dartsThrow.pid === playerId && dartsThrow.s === 180) {
      total180s++
    }
  })
  return total180s
}

function getHighCheckouts(playerId: number) {
  // If the game is concluded we get the information from there.
  if (props.game.concluded_at) {
    return props.game[`player${getPlayerIndex(playerId)}`]!.high_checkouts
  }

  // Otherwise we take it from the throws.
  let highCheckouts = ''
  props.game.throws.forEach((dartsThrow) => {
    if (dartsThrow.pid === playerId && dartsThrow.ft && dartsThrow.s > 99) {
      highCheckouts += dartsThrow.s + ', '
    }
  })
  return highCheckouts.slice(0, -2)
}

onMounted(() => {
  if (!props.game.concluded_at) {
    window.Echo.private(`game.${props.game.id}`).listen('GameThrowEvent', (game: Game) => {
      emit('gameUpdated', game)
    })
  }
})

onBeforeUnmount(() => {
  window.Echo.leave(`game.${props.game.id}`)
})

const dartsThrows = computed(() => {
  return convertApiThrowsToLocal(props.game.throws ?? [])
})
</script>

<template>
  <div class="flex flex-col justify-between gap-4">
    <UiAlert
      v-if="!game.started_at && !game.concluded_at"
      variant="warning"
      :title="$t('This game has not been started yet')"
    />
    <GamesGameStatsOverview
      :name="game.tournament_name ?? ''"
      :event-name="game.event_name"
      :starting-score="game.starting_score"
      :best-of="game.best_of"
      :legs-to-play="game.legs_to_play"
      :winner="winner ? winner.name : null"
      :bull-winner="bullWinner ? bullWinner.name : null"
      :player1="game.player1!.name"
      :player2="game.player2!.name"
      :player1-legs="game.player1!.score ?? 0"
      :player2-legs="game.player2!.score ?? 0"
      :player1-average="game.stats?.[0]?.avg ?? 0"
      :player2-average="game.stats?.[1]?.avg ?? 0"
      :player1-first9-average="game.stats?.[0]?.first9avg ?? 0"
      :player2-first9-average="game.stats?.[1]?.first9avg ?? 0"
      :player1-one-eighties="getOneEighties(game.player1!.id) ?? 0"
      :player2-one-eighties="getOneEighties(game.player2!.id) ?? 0"
      :player1-high-checkouts="getHighCheckouts(game.player1!.id) ?? ''"
      :player2-high-checkouts="getHighCheckouts(game.player2!.id) ?? ''"
      :player1-leg-darts-used="gameState.getLegDartsUsedFor(GamePlayerSimple.HOME)"
      :player2-leg-darts-used="gameState.getLegDartsUsedFor(GamePlayerSimple.VISITOR)"
    />

    <UiSeparator />

    <div v-if="isLive && game.throws.length === 0">
      {{ $t('Game will start soon ..') }}
    </div>
    <GamesGameLegsOverview
      v-else
      :participants="[
        game.player1!,
        game.player2!,
      ]"
      :darts-throws="dartsThrows"
      :starting-score="game.starting_score"
    />
  </div>
</template>
